import { VALUE_CHAIN_MAP_CUSTOM_NODES, VALUE_CHAIN_PANEL_NODES_TYPES } from '../constants/interfaces';
import {
  getBoardForSwimlane,
  isSectionValidForPanelType,
} from '../pages/CompanyAdminPages/CompanyMap/components/ValueChain/utils/valueChainSectionValidation';
import { getTargetedBoard } from './reactFlowHelpers';

import type { IOnDragOverByType } from './interfaces/IOnDragOverByType';
import type { IOnDragOverSwimlane } from './interfaces/IOnDragOverSwimlane';

// Swimlane drag over handler: set opacity to 0.6 when the node is dragged over the target group node
const onDragOverSwimlane = ({
  nodes, screenToFlowPosition, event, setNodes,
}: IOnDragOverSwimlane) => {
  const dragPosition = screenToFlowPosition({
    x: event.clientX,
    y: event.clientY,
  });

  getTargetedBoard(dragPosition, nodes);

  setNodes((prevNodes) => prevNodes.map((prevNode) => (
    { ...prevNode, data: { ...prevNode.data, isDragTarget: false } }
  )));

  const targetedBoard = getTargetedBoard(dragPosition, nodes);
  if (targetedBoard) {
    setNodes((prevNodes) => prevNodes.map((prevNode) => (prevNode.id === targetedBoard.id
      ? { ...prevNode, data: { ...prevNode.data, isDragTarget: true } }
      : prevNode)));
  }
};

// Profit Center / Customer / Supplier etc. drag over: highlight swimlane only if its board allows this card type
const onDragOverProfitCenter = ({
  nodes, screenToFlowPosition, event, setNodes, panelType,
}: IOnDragOverSwimlane) => {
  const dragPosition = screenToFlowPosition({
    x: event.clientX,
    y: event.clientY,
  });

  setNodes((prevNodes) => prevNodes.map((prevNode) => (
    { ...prevNode, data: { ...prevNode.data, isDragTarget: false } }
  )));

  const targetedSwimlane = nodes.find((node) => {
    if (node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE) {
      const {
        position, width, height, measured,
      } = node;
      const actualWidth = measured?.width || width || 262;
      const actualHeight = measured?.height || height || 800;
      return (
        dragPosition.x >= position.x
        && dragPosition.x <= position.x + actualWidth
        && dragPosition.y >= position.y
        && dragPosition.y <= position.y + actualHeight
      );
    }
    return false;
  });

  // Highlight the targeted swimlane only if it belongs to a board that allows this card type
  if (targetedSwimlane) {
    const shouldHighlight = !panelType
      ? true
      : (() => {
        const targetBoard = getBoardForSwimlane(nodes, targetedSwimlane);
        const boardName = targetBoard?.data?.name as string | undefined;
        return !!boardName && isSectionValidForPanelType(boardName, panelType);
      })();
    if (shouldHighlight) {
      setNodes((prevNodes) => prevNodes.map((prevNode) => (prevNode.id === targetedSwimlane.id
        ? { ...prevNode, data: { ...prevNode.data, isDragTarget: true } }
        : prevNode)));
    }
  }
};

const onDragOverByType = ({
  event, panelType, nodes, setNodes, screenToFlowPosition,
}: IOnDragOverByType) => {
  event.preventDefault();
  const { dataTransfer } = event;
  dataTransfer.dropEffect = 'move';

  if (panelType === VALUE_CHAIN_PANEL_NODES_TYPES.SWIMLANE) {
    onDragOverSwimlane({
      event, nodes, setNodes, screenToFlowPosition,
    });
  }

  if (panelType === VALUE_CHAIN_PANEL_NODES_TYPES.PROFIT_CENTER
    || panelType === VALUE_CHAIN_PANEL_NODES_TYPES.CUSTOMER
    || panelType === VALUE_CHAIN_PANEL_NODES_TYPES.SUPPLIER
    || panelType === VALUE_CHAIN_PANEL_NODES_TYPES.INPUT_PRODUCT
    || panelType === VALUE_CHAIN_PANEL_NODES_TYPES.BALANCE_SHEET_ASSET
  ) {
    onDragOverProfitCenter({
      event, nodes, setNodes, screenToFlowPosition, panelType,
    });
  }
};

export default onDragOverByType;
